import api, { methods } from "@api";
import Button from "@components/button";
import CustomImage from "@components/custom-image";
import CloseIcon from "@components/icons/close-icon";
import CutleryIcon from "@components/icons/cutlery-icon";
import PlusIcon from "@components/icons/plus-icon";
import SendIcon from "@components/icons/send-icon";
import SafeHorizontal from "@components/safe-horizontal";
import Spacer from "@components/Spacer";
import { useShoppingcartStore } from "@features/cart/shopping-cart-store";
import RecipeDetailView from "@features/recipe/recipe-detail-view";
import RecipeProductItem from "@features/recipe/recipe-product-item";
import useCustomWindowDimensions from "@hooks/use-custom-window-dimensions";
import { useProducts } from "@provider/app-data";
import { useAuth } from "@provider/context-auth";
import { roundness } from "@provider/dripsy";
import { useSafeArea } from "@provider/safe-area/use-safe-area";
import { toast } from "@provider/toast";
import { useMutation } from "@tanstack/react-query";
import { Text, TextInput, useSx, View } from "dripsy";
import * as React from "react";
import { ActivityIndicator, Modal, Platform, Pressable, ScrollView } from "react-native";
import { useBottomTabIndex, useNavigation } from "react-native-ridge-navigation";

import { BottomRoots } from "../../Navigator";
import routes from "../../NavigatorRoutes";
import { sendAssistantRecipeChat } from "./assistant-api";
import { useAssistant } from "./assistant-provider";
import type {
  AssistantAction,
  AssistantChatMessage,
  AssistantChatResponse,
  AssistantProductSuggestion,
  AssistantRecipe,
} from "./assistant-types";

type ConversationMessage =
  | {
      id: string;
      role: "user";
      content: string;
    }
  | {
      id: string;
      role: "assistant";
      content: string;
      response?: AssistantChatResponse;
    };

type ChatProduct = {
  id: number;
  pid: number;
  quantity: number;
  name: string;
  recipe_id: number;
  product_id: number;
};

export const ASSISTANT_DOCK_WIDTH = 540;
export const ASSISTANT_DOCK_BREAKPOINT = 1120;

function AssistantPanel() {
  const { isOpen, close } = useAssistant();
  const { width } = useCustomWindowDimensions();
  const sx = useSx();
  const isDocked = Platform.OS === "web" && width >= ASSISTANT_DOCK_BREAKPOINT;

  if (isDocked) {
    if (!isOpen) {
      return null;
    }

    return (
      <View
        style={sx({
          position: "fixed" as any,
          top: 0,
          right: 0,
          bottom: 0,
          width: ASSISTANT_DOCK_WIDTH,
          backgroundColor: "$background",
          borderLeftWidth: 1,
          borderColor: "$lightGrey",
          zIndex: 1000,
          overflow: "hidden",
        })}
      >
        <AssistantPanelContent />
      </View>
    );
  }

  return (
    <Modal transparent visible={isOpen} animationType="slide" onRequestClose={close}>
      <View sx={{ flex: 1, flexDirection: "row", justifyContent: "center" }}>
        <Pressable
          onPress={close}
          style={sx({
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.35)",
          })}
        />
        <View
          style={sx({
            marginTop: "auto",
            width: "100%",
            maxWidth: Platform.OS === "web" ? ASSISTANT_DOCK_WIDTH : "100%",
            height: "88%",
            backgroundColor: "$background",
            borderTopLeftRadius: roundness,
            borderTopRightRadius: roundness,
            borderColor: "$lightGrey",
            overflow: "hidden",
          })}
        >
          <AssistantPanelContent />
        </View>
      </View>
    </Modal>
  );
}

function getAssistantErrorMessage(error: any) {
  const status = error?.response?.status;

  if (status === 422) {
    return error?.message || "Stel eerst een vraag.";
  }

  if (status === 401) {
    return "Log in om DGV hulp te gebruiken.";
  }

  if (status === 403) {
    return "DGV hulp is nog niet beschikbaar voor dit account.";
  }

  if (status === 429) {
    return "Het is even druk in de keuken. Probeer het zo opnieuw.";
  }

  if (status === 404 || status === 502 || status === 503) {
    return "De hulp is even niet beschikbaar. Probeer het zo opnieuw.";
  }

  return "De hulp kon nu geen antwoord maken. Probeer het later opnieuw.";
}

function AssistantPanelContent() {
  const { close } = useAssistant();
  const sx = useSx();
  const [input, setInput] = React.useState("");
  const [messages, setMessages] = React.useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingPrompt, setLoadingPrompt] = React.useState("");
  const scrollRef = React.useRef<ScrollView>(null);
  const isSubmittingRef = React.useRef(false);
  const canSubmit = !isLoading && input.trim().length >= 2;

  const submit = async (suggestion?: string) => {
    const content = (suggestion || input).trim();
    if (content.length < 2 || isLoading || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    const userMessage: ConversationMessage = { id: `${Date.now()}-user`, role: "user", content };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setLoadingPrompt(content);
    setIsLoading(true);

    try {
      const apiMessages: AssistantChatMessage[] = nextMessages.map((message) => ({
        role: message.role,
        content: message.content,
      }));

      const response = await sendAssistantRecipeChat({
        messages: apiMessages,
        currentPath: getCurrentPath(),
      });

      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-assistant`,
          role: "assistant",
          content: response.assistant_text,
          response,
        },
      ]);
    } catch (error: any) {
      const message = getAssistantErrorMessage(error);
      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-assistant-error`,
          role: "assistant",
          content: message,
        },
      ]);
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
      setLoadingPrompt("");
    }
  };

  const submitOnEnter = (event: any) => {
    const isShiftEnter = event?.nativeEvent?.shiftKey || event?.shiftKey;
    if (Platform.OS !== "web" || event?.nativeEvent?.key !== "Enter" || isShiftEnter) {
      return;
    }

    event?.preventDefault?.();
    submit();
  };

  return (
    <View sx={{ flex: 1 }}>
      <View
        sx={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          px: "$3",
          py: "$2",
          borderBottomWidth: 1,
          borderColor: "$lightGrey",
          backgroundColor: "$secondaryBackground",
        }}
      >
        <View sx={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
          <View
            sx={{
              width: 38,
              height: 38,
              borderRadius: 19,
              backgroundColor: "$lightGreenOpacity12",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CutleryIcon width={16} height={24} />
          </View>
          <View sx={{ ml: "$2", flex: 1 }}>
            <Text sx={{ fontWeight: "700", fontSize: 18 }}>DGV hulp</Text>
            <Text sx={{ color: "$textGrey", fontSize: 12 }}>Recepten en boodschappen vinden</Text>
          </View>
        </View>
        <Pressable accessibilityRole="button" accessibilityLabel="Sluit DGV hulp" onPress={close} style={sx({ p: "$2" })}>
          <CloseIcon size={16} color="$textSecondary" />
        </Pressable>
      </View>

      <ScrollView
        ref={scrollRef}
        keyboardShouldPersistTaps="handled"
        onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: true })}
      >
        <SafeHorizontal>
          <View sx={{ p: "$3" }}>
            {messages.length === 0 ? <AssistantEmptyState onSubmit={submit} /> : null}
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            {isLoading ? <AssistantLoading prompt={loadingPrompt} /> : null}
          </View>
        </SafeHorizontal>
      </ScrollView>

      <View
        sx={{
          borderTopWidth: 1,
          borderColor: "$lightGrey",
          backgroundColor: "$secondaryBackground",
          px: "$2",
          pt: "$2",
          pb: "$2",
        }}
      >
        <View
          sx={{
            flexDirection: "row",
            alignItems: "flex-end",
            backgroundColor: "$background",
            borderWidth: 1,
            borderColor: "$secondaryBackgroundDarker",
            borderRadius: 26,
            px: "$2",
            py: 5,
          }}
        >
          <TextInput
            value={input}
            onChangeText={setInput}
            onKeyPress={submitOnEnter}
            onSubmitEditing={Platform.OS === "web" ? undefined : () => submit()}
            placeholder={isLoading ? "Even bezig..." : "Vraag om een recept of product..."}
            editable={!isLoading}
            sx={{
              flex: 1,
              minHeight: 42,
              maxHeight: 112,
              borderWidth: 0,
              px: 0,
              py: "$1",
              color: "$text",
              backgroundColor: "transparent",
              outlineStyle: "none",
              fontSize: 15,
              lineHeight: 20,
            }}
            multiline
            accessibilityLabel="Vraag aan de DGV hulp"
          />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Verstuur vraag"
            disabled={!canSubmit}
            onPress={() => submit()}
            style={sx({
              width: 42,
              height: 42,
              borderRadius: 21,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: canSubmit ? "$primary" : "$lightGreenOpacity",
              ml: "$1",
            })}
          >
            {isLoading ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <SendIcon size={20} color="$background" />
            )}
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function AssistantEmptyState({ onSubmit }: { onSubmit: (suggestion: string) => void }) {
  const sx = useSx();
  const suggestions = [
    {
      title: "Snel iets op tafel",
      description: "Makkelijk, warm en weinig afwas.",
      prompt: "Maak een snelle avondmaaltijd met weinig afwas voor 2 personen",
    },
    {
      title: "Artikel zoeken",
      description: "Bijvoorbeeld havermout, kaas of iets glutenvrij.",
      prompt: "Ik zoek een paar passende producten voor ontbijt",
    },
    {
      title: "Koken met wat er nog ligt",
      description: "Restjes, losse groenten of voorraad opmaken.",
      prompt: "Maak een recept waarmee ik restjes en losse groenten kan opmaken",
    },
    {
      title: "Lekker veel groente",
      description: "Seizoensgroenten in de hoofdrol.",
      prompt: "Maak een recept met veel seizoensgroenten voor 4 personen",
    },
    {
      title: "Vegetarisch voor iedereen",
      description: "Vullend, fris en geschikt voor de hele tafel.",
      prompt: "Maak een vegetarisch hoofdgerecht voor 4 personen",
    },
  ];
  const popularNeeds = [
    { label: "Zoek zuivel", prompt: "Welke zuivelproducten passen goed bij ontbijt?" },
    { label: "Binnen 30 min.", prompt: "Maak een recept dat binnen 30 minuten klaar is" },
    { label: "Kindvriendelijk", prompt: "Maak een kindvriendelijk recept met DGV boodschappen" },
    { label: "Glutenvrij zoeken", prompt: "Help me met het zoeken naar glutenvrije producten" },
    { label: "Seizoen", prompt: "Welke seizoensgroenten kan ik nu gebruiken?" },
  ];

  return (
    <View sx={{ py: "$3" }}>
      <View
        sx={{
          backgroundColor: "$secondaryBackground",
          borderWidth: 1,
          borderColor: "$secondaryBackgroundDarker",
          borderRadius: roundness,
          p: "$3",
          flexDirection: "row",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <View
          sx={{
            width: 92,
            height: 92,
            borderRadius: 46,
            backgroundColor: "$lightGreenOpacity12",
            alignItems: "center",
            justifyContent: "center",
            mr: "$3",
          }}
        >
          <CustomImage
            source={require("../../assets/vegetable.png")}
            sx={{ width: 78, height: 78, borderRadius: 39 }}
            alt="Groenten"
          />
        </View>
        <View sx={{ flex: 1 }}>
          <Text sx={{ fontWeight: "700", fontSize: 22, lineHeight: 26 }}>Waarmee kan ik helpen?</Text>
          <Text sx={{ color: "$textGrey", mt: "$1", lineHeight: 20 }}>
            Vind snel een artikel, laat producten vergelijken of maak een recept met boodschappen uit de winkel.
          </Text>
        </View>
      </View>
      <Spacer />

      <Text sx={{ fontWeight: "700", fontSize: 16 }}>Populaire wensen</Text>
      <Spacer size="$1" />
      <View sx={{ flexDirection: "row", flexWrap: "wrap" }}>
        {popularNeeds.map((item) => (
          <Pressable
            key={item.label}
            accessibilityRole="button"
            accessibilityLabel={item.label}
            onPress={() => onSubmit(item.prompt)}
            style={sx({
              borderWidth: 1,
              borderColor: "$secondaryBackgroundDarker",
              backgroundColor: "$background",
              borderRadius: 999,
              px: "$2",
              py: "$1",
              mr: "$1",
              mb: "$1",
            })}
          >
            <Text sx={{ color: "$primary", fontWeight: "700", fontSize: 13 }}>{item.label}</Text>
          </Pressable>
        ))}
      </View>

      <Spacer />
      {suggestions.map((suggestion) => (
        <View key={suggestion.title} sx={{ mb: "$1" }}>
          <Button
            outline
            titleColor="$primary"
            description={suggestion.description}
            onPress={() => onSubmit(suggestion.prompt)}
            justifyContent="flex-start"
            py="$1"
          >
            {suggestion.title}
          </Button>
        </View>
      ))}
    </View>
  );
}

function MessageBubble({ message }: { message: ConversationMessage }) {
  const isUser = message.role === "user";

  return (
    <View sx={{ alignItems: isUser ? "flex-end" : "flex-start", mb: "$3" }}>
      <View
        sx={{
          maxWidth: "92%",
          backgroundColor: isUser ? "$primary" : "$secondaryBackground",
          borderRadius: roundness,
          borderBottomRightRadius: isUser ? 2 : roundness,
          borderBottomLeftRadius: isUser ? roundness : 2,
          borderWidth: isUser ? 0 : 1,
          borderColor: "$secondaryBackgroundDarker",
          p: "$2",
        }}
      >
        <Text sx={{ color: isUser ? "$background" : "$text", lineHeight: 21 }}>{message.content}</Text>
      </View>
      {!isUser && message.response ? <AssistantResponse response={message.response} /> : null}
    </View>
  );
}

function AssistantResponse({ response }: { response: AssistantChatResponse }) {
  const hasRecipe = !!response.recipe?.title;
  const hasStandaloneProducts = !hasRecipe && response.products.length > 0;

  return (
    <View sx={{ alignSelf: "stretch", mt: "$2" }}>
      {hasRecipe ? <GeneratedRecipeCard recipe={response.recipe} products={response.products} /> : null}
      {hasStandaloneProducts ? (
        <AssistantProductsList
          products={response.products}
          title={response.mode === "product_search" ? "Gevonden boodschappen" : "Aanbevolen boodschappen"}
        />
      ) : null}
      {response.actions.length > 0 ? <AssistantActions actions={response.actions} /> : null}
    </View>
  );
}

function GeneratedRecipeCard({
  recipe,
  products,
}: {
  recipe: AssistantRecipe;
  products: AssistantProductSuggestion[];
}) {
  return (
    <View
      sx={{
        backgroundColor: "$secondaryBackground",
        borderRadius: roundness,
        borderWidth: 1,
        borderColor: "$secondaryBackgroundDarker",
        overflow: "hidden",
      }}
    >
      <View sx={{ p: "$3", backgroundColor: "$primary" }}>
        <Text sx={{ color: "$background", opacity: 0.82, fontWeight: "700", fontSize: 12 }}>DGV RECEPT</Text>
        <Spacer size="$1" />
        <Text sx={{ color: "$background", fontWeight: "700", fontSize: 22 }}>{recipe.title}</Text>
        <Spacer size="$1" />
        <RecipeDetailView recipe={recipe as any} isSmall />
      </View>
      <View sx={{ p: "$3" }}>
        {recipe.tags.length > 0 ? (
          <View sx={{ flexDirection: "row", flexWrap: "wrap", mb: "$2" }}>
            {recipe.tags.map((tag) => (
              <View
                key={tag}
                sx={{
                  backgroundColor: "$lightGreenOpacity12",
                  borderRadius: roundness / 2,
                  px: "$1",
                  py: 4,
                  mr: "$1",
                  mb: "$1",
                }}
              >
                <Text sx={{ color: "$primary", fontWeight: "600", fontSize: 12 }}>{tag}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {recipe.simple_description ? (
          <Text sx={{ color: "$textGrey", lineHeight: 20 }}>{recipe.simple_description}</Text>
        ) : null}

        <Spacer />
        <Text sx={{ fontWeight: "700", fontSize: 17 }}>Ingrediënten</Text>
        <Spacer size="$1" />
        <View sx={{ borderWidth: 1, borderColor: "$secondaryBackgroundDarker", borderRadius: roundness, overflow: "hidden" }}>
          {recipe.ingredients.map((ingredient, index) => (
            <View
              key={`${ingredient.name}-${index}`}
              sx={{
                flexDirection: "row",
                px: "$2",
                py: "$1",
                backgroundColor: index % 2 === 0 ? "$background" : "$secondaryBackground",
              }}
            >
              <Text sx={{ width: 92, color: "$textGrey", fontWeight: "600" }}>
                {ingredient.quantity ? `${formatQuantity(ingredient.quantity)} ${ingredient.unit}` : ""}
              </Text>
              <Text sx={{ flex: 1, lineHeight: 20 }}>
                {ingredient.name}
                {ingredient.product_name ? ` (${ingredient.product_name})` : ""}
              </Text>
            </View>
          ))}
        </View>

        <Spacer />
        <Text sx={{ fontWeight: "700", fontSize: 17 }}>Bereiding</Text>
        <Spacer size="$1" />
        {recipe.preparations.map((preparation) => (
          <View key={`${preparation.step}-${preparation.title}`} sx={{ flexDirection: "row", mb: "$2" }}>
            <View
              sx={{
                width: 26,
                height: 26,
                borderRadius: 13,
                backgroundColor: "$lightGreenOpacity12",
                alignItems: "center",
                justifyContent: "center",
                mr: "$2",
              }}
            >
              <Text sx={{ fontWeight: "700", color: "$primary", fontSize: 13 }}>{preparation.step}</Text>
            </View>
            <Text sx={{ flex: 1, lineHeight: 22, pt: 2 }}>{preparation.title}</Text>
          </View>
        ))}

        <AssistantProductsList recipe={recipe} products={products} title="Producten" />
      </View>
    </View>
  );
}

function AssistantProductsList({
  recipe,
  products,
  title,
}: {
  recipe?: AssistantRecipe;
  products: AssistantProductSuggestion[];
  title: string;
}) {
  const { data: allProducts } = useProducts();
  const { isAuthenticated } = useAuth();
  const { push } = useNavigation();
  const { switchToTab } = useBottomTabIndex();
  const { close } = useAssistant();
  const { width } = useCustomWindowDimensions();
  const shouldCloseAfterNavigation = Platform.OS !== "web" || width < ASSISTANT_DOCK_BREAKPOINT;
  const [chatProducts, setChatProducts] = React.useState<ChatProduct[]>([]);

  React.useEffect(() => {
    setChatProducts(
      products.map((item) => {
        const ingredient = recipe?.ingredients.find((ing) => ing.product_id === item.product_id);
        const product = allProducts.find((prod) => prod.id === item.product_id);
        const reason = getProductReasonLabel(item.reason, product?.pnm);
        return {
          id: item.product_id,
          pid: item.pid,
          product_id: item.product_id,
          quantity: clampProductQuantity(item.quantity),
          name: recipe ? "" : reason || ingredient?.name || "",
          recipe_id: 0,
        };
      }),
    );
  }, [products, recipe?.ingredients, allProducts]);

  const { mutate: addProducts, isPending } = useMutation({
    mutationFn: () =>
      api<{ group: "success" }>({
        path: "/shopping-cart/add-recipe-products",
        method: methods.POST,
        body: {
          products: chatProducts
            .filter((item) => item.quantity > 0)
            .map((item) => ({
              pid: item.pid,
              product_id: item.product_id,
              quantity: item.quantity,
              recipe_id: 0,
            })),
        },
      }),
    onSuccess: () => {
      useShoppingcartStore.getState().load();
      switchToTab(BottomRoots.Cart);
      if (shouldCloseAfterNavigation) {
        close();
      }
    },
    onError: () => {
      toast.show("Er is iets fout gegaan. Probeer het later opnieuw.");
    },
  });

  if (chatProducts.length === 0) {
    return null;
  }

  return (
    <>
      <Spacer />
      <Text sx={{ fontWeight: "700", fontSize: 17 }}>{title}</Text>
      <Spacer size="$1" />
      {chatProducts.map((item) => {
        const product = allProducts.find((prod) => prod.id === item.product_id);
        return (
          <RecipeProductItem
            key={`${item.product_id}-${item.pid}`}
            product={product}
            ingredient={item}
            labelPrefix=""
            labelPlacement="inside"
            onChange={(quantity) => {
              setChatProducts((current) =>
                current.map((currentItem) =>
                  currentItem.product_id === item.product_id
                    ? { ...currentItem, quantity: clampProductQuantity(quantity, true) }
                    : currentItem,
                ),
              );
            }}
          />
        );
      })}
      <Spacer />
      <Button
        isLoading={isPending}
        onPress={() => {
          if (!isAuthenticated) {
            push(routes.LoginOrSignupScreen, {});
            if (shouldCloseAfterNavigation) {
              close();
            }
            return;
          }
          addProducts();
        }}
        left={<PlusIcon color="$background" size={22} />}
      >
        {isAuthenticated ? "Voeg producten toe aan mand" : "Log eerst in"}
      </Button>
    </>
  );
}

function AssistantActions({ actions }: { actions: AssistantAction[] }) {
  const { push } = useNavigation();
  const { switchToTab } = useBottomTabIndex();
  const { close } = useAssistant();
  const { width } = useCustomWindowDimensions();
  const shouldCloseAfterNavigation = Platform.OS !== "web" || width < ASSISTANT_DOCK_BREAKPOINT;

  const closeAfterNavigation = () => {
    if (shouldCloseAfterNavigation) {
      close();
    }
  };

  const handleAction = (action: AssistantAction) => {
    if (action.type === "open_cart") {
      switchToTab(BottomRoots.Cart);
      closeAfterNavigation();
      return;
    }
    if (action.type === "open_products") {
      switchToTab(BottomRoots.Products);
      closeAfterNavigation();
      return;
    }
    if (action.type === "open_recipes") {
      push(routes.RecipesScreen, {});
      closeAfterNavigation();
      return;
    }
    if (action.type === "open_product" && action.product_id) {
      push(routes.ProductDetailScreen, { id: `${action.product_id}` });
      closeAfterNavigation();
    }
  };

  return (
    <View sx={{ flexDirection: "row", flexWrap: "wrap", mt: "$2" }}>
      {actions.map((action, index) => (
        <View key={`${action.type}-${action.product_id}-${index}`} sx={{ mr: "$1", mb: "$1" }}>
          <Button outline titleColor="$primary" py="$1" px="$2" onPress={() => handleAction(action)}>
            {action.label}
          </Button>
        </View>
      ))}
    </View>
  );
}

type AssistantLoadingMessage = {
  label: string;
  title: string;
  description: string;
};

function AssistantLoading({ prompt }: { prompt: string }) {
  const messages = React.useMemo(() => getAssistantLoadingMessages(prompt), [prompt]);
  const [messageIndex, setMessageIndex] = React.useState(0);
  const activeMessage = messages[messageIndex % messages.length];

  React.useEffect(() => {
    setMessageIndex(0);
    const interval = setInterval(() => {
      setMessageIndex((current) => current + 1);
    }, 1800);

    return () => clearInterval(interval);
  }, [messages.length, prompt]);

  return (
    <View sx={{ alignItems: "flex-start", mb: "$3" }}>
      <View
        sx={{
          maxWidth: "92%",
          flexDirection: "row",
          alignItems: "center",
          backgroundColor: "$secondaryBackground",
          borderRadius: roundness,
          borderBottomLeftRadius: 2,
          borderWidth: 1,
          borderColor: "$secondaryBackgroundDarker",
          p: "$2",
        }}
      >
        <View
          sx={{
            width: 34,
            height: 34,
            borderRadius: 17,
            backgroundColor: "$lightGreenOpacity12",
            alignItems: "center",
            justifyContent: "center",
            mr: "$2",
          }}
        >
          <ActivityIndicator size="small" color="#237A39" />
        </View>
        <View sx={{ flex: 1 }}>
          <Text sx={{ color: "$primary", fontWeight: "700", fontSize: 11, letterSpacing: 0 }}>{activeMessage.label}</Text>
          <Text sx={{ fontWeight: "700", color: "$text" }}>{activeMessage.title}</Text>
          <Text sx={{ color: "$textGrey", fontSize: 13, lineHeight: 18, mt: 2 }}>{activeMessage.description}</Text>
        </View>
        <LoadingDots index={messageIndex} />
      </View>
    </View>
  );
}

function LoadingDots({ index }: { index: number }) {
  return (
    <View sx={{ flexDirection: "row", alignItems: "center", ml: "$2", width: 30, justifyContent: "space-between" }}>
      {[0, 1, 2].map((dot) => (
        <View
          key={dot}
          sx={{
            width: 6,
            height: 6,
            borderRadius: 3,
            backgroundColor: "$primary",
            opacity: index % 3 === dot ? 1 : 0.28,
          }}
        />
      ))}
    </View>
  );
}

function getAssistantLoadingMessages(prompt: string): AssistantLoadingMessage[] {
  if (isRecipePrompt(prompt)) {
    return [
      {
        label: "Aan het fornuis",
        title: "Kijkt wat goed samenwerkt",
        description: "Ingredienten, hoeveelheden en producten worden rustig naast elkaar gelegd.",
      },
      {
        label: "In de pan",
        title: "Maakt er iets haalbaars van",
        description: "Geen keukentoeren, wel duidelijke stappen voor straks aan tafel.",
      },
      {
        label: "Langs de schappen",
        title: "Zoekt de boodschappen erbij",
        description: "Waar het kan worden ingredienten gekoppeld aan producten uit de winkel.",
      },
      {
        label: "Nog even proeven",
        title: "Controleert de details",
        description: "Porties, bereidingstijd en dieetwensen krijgen een laatste rondje aandacht.",
      },
    ];
  }

  if (isProductSearchPrompt(prompt)) {
    return [
      {
        label: "In het schap",
        title: "Zoekt in het assortiment",
        description: "Productnamen, categorieen en omschrijvingen worden met elkaar vergeleken.",
      },
      {
        label: "Even sorteren",
        title: "Filtert de beste matches",
        description: "Alleen producten die echt passen komen straks naar voren.",
      },
      {
        label: "Mandje klaar",
        title: "Zet de opties klaar",
        description: "Met aantallen en snelle knoppen voor de volgende stap.",
      },
    ];
  }

  return [
    {
      label: "Op zoek",
      title: "Leest je vraag",
      description: "DGV hulp zoekt de handigste route door winkel, recepten en artikelen.",
    },
    {
      label: "Context erbij",
      title: "Haalt de juiste context erbij",
      description: "Producten, categorieen en pagina-informatie worden gecombineerd.",
    },
    {
      label: "Netjes afronden",
      title: "Maakt een helder antwoord",
      description: "Kort genoeg om snel te lezen, compleet genoeg om verder te kunnen.",
    },
  ];
}

function isRecipePrompt(prompt: string) {
  return /\b(recept|recepten|maaltijd|gerecht|koken|kook|bereid|bereiding|ingredient|ingredienten|avondeten|diner|lunch|pasta|soep|salade|ovenschotel)\b/i.test(
    prompt,
  );
}

function isProductSearchPrompt(prompt: string) {
  return /\b(zoek|zoeken|hebben|artikel|artikelen|product|producten|assortiment|categorie|waar vind|welke)\b/i.test(
    prompt,
  );
}

function AssistantFloatingButton() {
  const { isOpen, open } = useAssistant();
  const insets = useSafeArea();
  const sx = useSx();
  const { width } = useCustomWindowDimensions();
  const shouldShowOnWeb = Platform.OS === "web" && width < ASSISTANT_DOCK_BREAKPOINT;

  if ((Platform.OS === "web" && !shouldShowOnWeb) || isOpen) {
    return null;
  }

  return (
    <View
      style={sx({
        position: "absolute",
        right: insets.right + 14,
        bottom: insets.bottom + 86,
        zIndex: 500,
        shadowColor: "$text",
        shadowOpacity: 0.18,
        shadowRadius: 12,
        elevation: 8,
      })}
    >
      <Button onPress={open} left={<CutleryIcon width={16} height={24} />}>
        DGV hulp
      </Button>
    </View>
  );
}

function formatQuantity(quantity: number) {
  if (Number.isInteger(quantity)) {
    return `${quantity}`;
  }
  return `${Math.round(quantity * 10) / 10}`;
}

function clampProductQuantity(quantity: number, allowZero = false) {
  const minimum = allowZero ? 0 : 1;
  if (!Number.isFinite(quantity)) {
    return minimum;
  }

  return Math.min(20, Math.max(minimum, Math.round(quantity)));
}

function getProductReasonLabel(reason: string, productName?: string) {
  const label = reason.replace(/^(voor|tip)\s+/i, "").trim();
  if (!label) {
    return "";
  }

  if (productName && label.localeCompare(productName, undefined, { sensitivity: "accent" }) === 0) {
    return "";
  }

  return label;
}

function getCurrentPath() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.pathname || "";
}

export { AssistantFloatingButton };
export default AssistantPanel;
